// Updated per olives brief: types+brands, hero-only, dropdown minimal, Products page full details.
import { useState } from 'react';
import { HoveredLink, Menu, MenuItem } from '@/components/ui/navbar-menu';
import { useLanguage } from '@/contexts/LanguageContext';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { oliveData } from '@/data/olives';
import { packagingData } from '@/data/packaging';

type NavItem = {
  path: string;
  label: string;
};

type NavbarMenuProps = {
  navItems: NavItem[];
  buildPath: (path: string) => string;
  isActive: (path: string) => boolean;
  className?: string;
};

const NavbarMenu = ({ navItems, buildPath, isActive, className }: NavbarMenuProps) => {
  const [active, setActive] = useState<string | null>(null);
  const { language, setLanguage, t } = useLanguage();

  const productsLabel =
    navItems.find((item) => item.path === '/collections')?.label ?? t('Колекции', 'Collections');
  const packagingLabel =
    navItems.find((item) => item.path === '/packaging')?.label ?? t('Опаковки', 'Packaging');

  const translate = <T extends { bg: string; en: string }>(value: T) => t(value.bg, value.en);

  const getAnchorHref = (anchor: string) => {
    const base = '/collections';
    const query = language === "en" ? "?lang=en" : "";
    return `${base}/${anchor}${query}`;
  };

  return (
    <div className={cn('relative', className)}>
      <Menu setActive={setActive} className="items-center space-x-6 text-sm font-medium">
        {navItems.map((item) => {
          if (item.path === '/collections') {
            return (
              <MenuItem
                key={item.path}
                id="products-menu"
                active={active}
                setActive={setActive}
                item={productsLabel}
                href={buildPath(item.path)}
              >
                <div className="flex min-w-[18rem] gap-6 text-left sm:min-w-[22rem]" aria-label={productsLabel}>
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold uppercase tracking-wide text-secondary">
                      {t('Маслини', 'Olives')}
                    </h3>
                    <ul className="mt-3 space-y-4">
                      <li>
                        <h4 className="mb-2 text-xs font-bold uppercase text-primary/80">{t('Гръцки', 'Greek')}</h4>
                        <ul className="space-y-2">
                          {oliveData.types
                            .filter((type) => type.country === 'greek')
                            .map((type) => (
                              <li key={type.id}>
                                <HoveredLink
                                  to={getAnchorHref(type.id)}
                                  className="block rounded-md px-2 py-1 text-sm text-slate-700 hover:bg-secondary/10 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
                                >
                                  {translate(type.name)}
                                </HoveredLink>
                              </li>
                            ))}
                        </ul>
                      </li>
                      <li>
                        <h4 className="mb-2 text-xs font-bold uppercase text-primary/80">{t('Турски', 'Turkish')}</h4>
                        <ul className="space-y-2">
                          {oliveData.types
                            .filter((type) => type.country === 'turkish')
                            .map((type) => (
                              <li key={type.id}>
                                <HoveredLink
                                  to={getAnchorHref(type.id)}
                                  className="block rounded-md px-2 py-1 text-sm text-slate-700 hover:bg-secondary/10 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
                                >
                                  {translate(type.name)}
                                </HoveredLink>
                              </li>
                            ))}
                        </ul>
                      </li>
                    </ul>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold uppercase tracking-wide text-secondary">
                      {t('Маслинови масла', 'Olive Oils')}
                    </h3>
                    <ul className="mt-3 space-y-2">
                      {oliveData.brands.map((brand) => (
                        <li key={brand.id}>
                          <a
                            href={getAnchorHref(brand.id)}
                            className="block rounded-md px-2 py-1 text-sm text-slate-700 transition-colors hover:bg-secondary/10 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
                          >
                            {translate(brand.name)}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </MenuItem>
            );
          }

          if (item.path === '/packaging') {
            return (
              <MenuItem
                key={item.path}
                id="packaging-menu"
                active={active}
                setActive={setActive}
                item={packagingLabel}
                href={buildPath(item.path)}
              >
                <div className="min-w-[12rem] text-left" aria-label={packagingLabel}>
                  <ul className="space-y-2">
                    {packagingData.map((packaging) => (
                      <li key={packaging.id}>
                        <HoveredLink
                          to={buildPath(`/packaging/${packaging.id}`)}
                          className="block rounded-md px-2 py-1 text-sm text-slate-700 hover:bg-secondary/10 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
                        >
                          {translate(packaging.title)}
                        </HoveredLink>
                      </li>
                    ))}
                  </ul>
                </div>
              </MenuItem>
            );
          }

          return (
            <HoveredLink
              key={item.path}
              to={buildPath(item.path)}
              className={cn(
                'rounded-full px-3 py-1 transition-colors',
                isActive(item.path) ? 'text-primary' : 'text-slate-700 hover:text-slate-900',
              )}
            >
              {item.label}
            </HoveredLink>
          );
        })}
        <div className="flex items-center space-x-2 border-l border-border/60 pl-4">
          <Button
            variant={language === 'bg' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setLanguage('bg')}
            className="text-sm"
          >
            BG
          </Button>
          <Button
            variant={language === 'en' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setLanguage('en')}
            className="text-sm"
          >
            EN
          </Button>
        </div>
      </Menu>
    </div>
  );
};

export default NavbarMenu;
